import { useState, useEffect, useRef } from 'react';
import { env } from '@/env';

const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;

export interface VersionInfo {
  version: string;
  buildNumber: string;
  buildId: string;
}

export interface VersionCheckResult {
  updateAvailable: boolean;
  remoteVersion: VersionInfo | null;
}

function hasNewVersion(remote: VersionInfo): boolean {
  if (remote.version !== env.APP_VERSION) return true;
  if (remote.buildNumber !== env.BUILD_NUMBER) return true;
  if (remote.buildId !== env.BUILD_ID) return true;
  return false;
}

export function useVersionCheck(): VersionCheckResult {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [remoteVersion, setRemoteVersion] = useState<VersionInfo | null>(null);
  const detectedRef = useRef(false);

  useEffect(() => {
    if (env.APP_VERSION === 'local') return;

    async function checkVersion() {
      try {
        const res = await fetch('/version.json', { cache: 'no-store' });
        const data: VersionInfo = await res.json();

        if (hasNewVersion(data) && !detectedRef.current) {
          detectedRef.current = true;
          setRemoteVersion(data);
          setUpdateAvailable(true);
        }
      } catch {
        // Network errors are silently ignored
      }
    }

    checkVersion();
    const id = setInterval(checkVersion, VERSION_CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  return { updateAvailable, remoteVersion };
}
